import {useRef} from 'react';

import {Badge, SimpleGrid} from '@mantine/core';
import {NavLink} from 'react-router-dom';

import {useProducts} from '../hooks/useProducts.ts';
import {useProductsSelection} from '../hooks/useProductsSelection.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {Product, ProductStatus} from '../types';
import {formatTimestamp} from "../../../utils/TableTimestamps.tsx";
import {I18n} from "@aws-amplify/core";

interface RecordsTableProps {
    statusFilter?: ProductStatus | null;
}

export const RecordsTable = ({statusFilter}: RecordsTableProps) => {
    const {selectedRecords, setSelectedRecords} = useProductsSelection();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProducts(statusFilter);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const columns: DataTableColumn<Product>[] = [
        {
            accessor: 'name',
            title: I18n.get('Name'),
            width: '1fr',
            render: (record: Product) => (
                <NavLink to={`/products/${record.id}`}>{record.name}</NavLink>
            ),
        },
        {
            accessor: 'status',
            title: I18n.get('Status'),
            width: '120px',
            render: (item: Product) => (
                <Badge
                    color={item.status === 'Active' ? 'green' : item.status === 'Draft' ? 'yellow' : 'red'}
                    variant="light"
                >
                    {item.status || 'UNKNOWN'}
                </Badge>
            ),
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Product) => formatTimestamp(item.created as string),
        },
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>
        </SimpleGrid>
    );
};
